<template>
  <div ref="select" class="select-search">
    <HSelect
      :id="id"
      :options="results"
      :option-key="valueKey"
      :option-label="valueLabel"
      :value="value"
      :label="label"
      :required="required"
      :disabled="disabled"
      :placeholder="placeholder"
      :multiple="multiple"
      :no-border="noBorder"
      @update:value="handleSelect"
      @input="search"
      @focusout="handleFocusout"
      @key-down="handleKeyDown"
    >
      <template v-if="!hideIcon" #iconLeft>
        <WIcon :icon="icon" />
      </template>

      <template #options="{ option, index }">
        <div
          class="select-search__option"
          :index="index"
          :class="{ focus: index === selectedIndex }"
        >
          <slot :result="option" :query="query" :results="results">
            <div>{{ option[valueLabel] }}</div>

            <div v-if="valueDetail">{{ option[valueDetail] }}</div>
          </slot>
        </div>
      </template>

      <template #iconRight>
        <WIcon v-show="loading" icon="icLoad" class="select-search__loading" />
      </template>
    </HSelect>
  </div>
</template>
<script>
import { WIcon } from '@alice-health/wonderland-vue'
import debounce from 'lodash/debounce'

import ArrowSelection from '@/wrappers/selection/ArrowSelection'

import { HSelect } from '..'
export default {
  components: { WIcon, HSelect },
  props: {
    id: {
      type: String,
      default: 'select-search'
    },
    value: {
      type: [Object, String, Array],
      default: undefined
    },
    label: {
      type: String,
      default: undefined
    },
    valueKey: {
      type: String,
      default: 'id'
    },
    valueLabel: {
      type: String,
      default: undefined
    },
    valueDetail: {
      type: String,
      default: undefined
    },
    required: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    placeholder: {
      type: String,
      default: ''
    },
    searchFunction: {
      type: Function,
      default: () => []
    },
    hideIcon: {
      type: Boolean
    },
    icon: {
      type: String,
      default: 'icSearch'
    },
    multiple: {
      type: Boolean
    },
    noBorder: {
      type: Boolean
    }
  },
  emits: ['input', 'onUpdateQuery'],
  data() {
    return {
      loading: false,
      results: [],
      abortController: null,
      selectedIndex: 0,
      arrowSelectionHandler: null,
      query: null
    }
  },
  mounted() {
    this.arrowSelectionHandler = new ArrowSelection({
      listReference: this.$refs.select,
      onEnter: this.handleEnter
    })
  },
  methods: {
    handleSelect(value) {
      this.$emit('input', value)
    },
    handleFocusout() {
      this.results = []
      this.loading = false
      this.selectedIndex = 0
    },
    handleEnter() {
      if (this.multiple) {
        const result = this.value
          ? [...this.value, this.results[this.selectedIndex]]
          : [this.results[this.selectedIndex]]
        this.handleSelect(result)
        return
      }
      this.handleSelect(this.results[this.selectedIndex])
    },
    handleKeyDown(event) {
      const index = this.arrowSelectionHandler.handleKeyDown(
        event,
        this.results.length,
        this.selectedIndex
      )
      if (index !== false) {
        this.selectedIndex = index
      }
      return false
    },
    search: debounce(async function (query) {
      let results = []
      this.query = query
      this.$emit('onUpdateQuery', query)
      if (!query || query.length <= 2) return

      this.abortController?.abort()
      this.abortController = new AbortController()
      this.loading = true
      results = await this.searchFunction(query, this.abortController.signal)
      this.selectedIndex = 0
      this.loading = false
      this.results = results || []
    }, 200)
  }
}
</script>
<style scoped lang="scss">
.select-search {
  &__option {
    display: flex;
    justify-content: space-between;
    &.focus {
      color: var(--gl-color-shades-magenta-60);
    }
  }
  &__loading {
    animation: select__animation-spin 1s linear infinite;
  }

  @keyframes select__animation-spin {
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
}
</style>
